import React, { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import { toast } from "sonner";
import {
  FileText,
  Upload,
  Plus,
  Trash2,
  ChevronDown,
  ChevronRight,
  Stethoscope,
  Info,
} from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import DisclaimerV1 from "@/components/DisclaimerV1";
import apiClient from "@/lib/apiClient";
import AnalysisProgress from "@/components/AnalysisProgress";
import AIPipelineMonitor from "@/components/AIPipelineMonitor";
import OCRComparisonPanel from "@/components/OCRComparisonPanel";

export default function AnalysisCompose() {
  const [searchParams] = useSearchParams();
  const patientId = searchParams.get("patientId") || "unknown_patient";
  const navigate = useNavigate();

  const [sources, setSources] = useState([]);
  const [anamnese, setAnamnese] = useState("");
  const [consent, setConsent] = useState(false);
  const [isLaunching, setIsLaunching] = useState(false);
  const [analysisId, setAnalysisId] = useState(null);
  const [lastPipelineMeta, setLastPipelineMeta] = useState(null);

  const isUploadingRef = useRef(false);

  // Progress tracking
  const [status, setStatus] = useState("idle");
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState("");
  const [patientData, setPatientData] = useState(null);

  useEffect(() => {
    if (patientId && patientId !== "unknown_patient") {
      apiClient.get(`/patients/${patientId}`)
        .then(res => setPatientData(res.data))
        .catch(err => {
          console.error("Failed to load patient", err);
        });
    }
  }, [patientId]);


  const docTypes = [
    { value: "ordonnance", label: "Ordonnance" },
    { value: "crh", label: "Compte-rendu" },
    { value: "biologie", label: "Biologie" },
    { value: "lettre_liaison", label: "Lettre de liaison" },
    { value: "autre", label: "Autre" }
  ];


  const getUploadErrorMsg = (err, step) => {
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail;
    if (!status) return `${step} — impossible de contacter le serveur`;
    if (status === 400) return detail || `${step} — format de fichier invalide`;
    if (status === 413) return `${step} — fichier trop volumineux (max 10 Mo)`;
    if (status === 422) return `${step} — requête mal formée (contacter l'équipe)`;
    if (status === 429) return `${step} — trop de requêtes, patientez 1 minute`;
    if (status === 500) return `${step} — ${detail || "erreur serveur"}`;
    return detail || `${step} — erreur ${status}`;
  };

  const onDrop = useCallback(async (acceptedFiles) => {
    if (isUploadingRef.current) {
      toast.warning("Upload en cours — attendez la fin avant d'ajouter un document");
      return;
    }
    // 1. Valider et préparer tous les fichiers
    const validFiles = [];
    for (const file of acceptedFiles) {
      if (!file.name.toLowerCase().endsWith('.pdf')) {
        toast.error(`Seuls les PDF sont acceptés : ${file.name}`);
        continue;
      }
      if (file.size > 40 * 1024 * 1024) {
        toast.error(`Fichier trop volumineux (max 40 Mo) : ${file.name}`);
        continue;
      }
      validFiles.push(file);
    }
    if (validFiles.length === 0) return;

    // 2. Afficher TOUS les fichiers immédiatement avec statut "en attente"
    //    → l'utilisateur voit d'emblée combien de documents sont en traitement
    const newSources = validFiles.map((file, i) => ({
      id: `${Date.now()}-${i}-${Math.random().toString(36).substring(7)}`,
      type: "pdf",
      doc_type: "autre",
      doc_date: new Date().toISOString().split('T')[0],
      file_name: file.name,
      text: "",
      medications: [],
      reliability: "objective",
      status: "uploading",
      statusMsg: validFiles.length > 1 ? `En attente (${i + 1}/${validFiles.length})` : "En attente...",
    }));
    setSources(prev => [...prev, ...newSources]);

    // 3. Traiter chaque fichier séquentiellement
    isUploadingRef.current = true;
    for (let i = 0; i < validFiles.length; i++) {
      const file = validFiles[i];
      const tempId = newSources[i].id;

      const setMsg = (msg) =>
        setSources(prev => prev.map(s => s.id === tempId ? { ...s, statusMsg: msg } : s));

      try {
        const formData = new FormData();
        formData.append("file", file);

        // Étapes progressives pendant l'upload (simulées — 1 seul appel HTTP)
        const UPLOAD_STEPS = [
          { delay: 0,    label: "Envoi du fichier..." },
          { delay: 1200, label: "Récupération du document..." },
          { delay: 2800, label: "OCR local (Tesseract)..." },
          { delay: 6000, label: "Anonymisation du texte..." },
          { delay: 8500, label: "Extraction des médicaments..." },
          { delay: 11000, label: "Agrégation et finalisation..." },
        ];
        const stepTimers = [];
        UPLOAD_STEPS.forEach(({ delay, label }) => {
          stepTimers.push(setTimeout(() => setMsg(label), delay));
        });

        let uploadRes;
        try {
          uploadRes = await apiClient.post("/analyses/upload-pdf", formData);
        } finally {
          stepTimers.forEach(clearTimeout);
        }
        const extractedText = uploadRes.data.extracted_text || "";
        const extractedMedications = uploadRes.data.medications || [];

        // AI Pipeline Monitor — capturer les méta du dernier upload
        if (uploadRes.data.ocr_comparison || uploadRes.data.extraction_meta) {
          setLastPipelineMeta({
            ocrComparison: uploadRes.data.ocr_comparison || null,
            extractionMeta: uploadRes.data.extraction_meta || null,
            glinerMeta: uploadRes.data.gliner_meta || null,
          });
        }

        let docType = "autre";
        let docDate = new Date().toISOString().split('T')[0];

        if (extractedText) {
          try {
            setMsg("Classification du document...");
            const classifyRes = await apiClient.post("/analyses/classify-document", {
              text_preview: extractedText.substring(0, 2000)
            });
            docType = classifyRes.data.doc_type || "autre";
            if (classifyRes.data.doc_date) {
              docDate = classifyRes.data.doc_date;
            }
          } catch (classifyErr) {
            console.warn("Classification non bloquante :", getUploadErrorMsg(classifyErr, "Classification"));
          }
        }

        setSources(prev => prev.map(s => s.id === tempId ? {
          ...s,
          text: extractedText,
          medications: extractedMedications,
          doc_type: docType,
          doc_date: docDate,
          ocr_comparison: uploadRes.data.ocr_comparison || null,
          status: "ready",
          statusMsg: "",
        } : s));

      } catch (err) {
        const errMsg = getUploadErrorMsg(err, "Upload");
        console.error("Upload error", err);
        toast.error(`${file.name} — ${errMsg}`);
        // Marquer en erreur (pas supprimer) pour que l'utilisateur voie quel fichier a échoué
        setSources(prev => prev.map(s => s.id === tempId ? {
          ...s,
          status: "error",
          statusMsg: errMsg,
        } : s));
      }
    }
    isUploadingRef.current = false;
  }, []);


  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, accept: {'application/pdf': ['.pdf']} });

  const removeSource = (id) => {
    setSources(prev => prev.filter(s => s.id !== id));
  };

  const updateSource = (id, field, value) => {
    setSources(prev => prev.map(s => s.id === id ? { ...s, [field]: value } : s));
  };

  const handleLaunch = async () => {
    if (!consent) return;

    setIsLaunching(true);
    setStatus("launching");

    try {
      const finalSources = [
        ...sources.map(s => ({
          type: s.type,
          doc_type: s.doc_type,
          doc_date: s.doc_date,
          reliability: s.reliability,
          text: s.text
        }))
      ];

      if (anamnese.trim()) {
        finalSources.push({
          type: "freetext",
          doc_type: "anamnese",
          reliability: "declarative",
          text: anamnese.trim()
        });
      }

      // Merge medications pre-extracted from each PDF upload so the orchestrator
      // can skip the duplicate Gemini extraction call (saves 1 quota slot per PDF).
      const preExtractedMeds = sources.flatMap(s => s.medications || []);
      const dedupedMeds = Array.from(
        new Map(preExtractedMeds.map(m => [m.name?.toLowerCase(), m])).values()
      );

      const basePatient = patientData
        ? { ...patientData, pdf_content: null }
        : { id: patientId, age: 75, sex: "M", medical_history: [], acute_pathologies: [], medications: [], allergies: [], pdf_content: null };

      if (dedupedMeds.length > 0) {
        basePatient.medications = [
          ...dedupedMeds,
          ...(basePatient.medications || []).filter(
            m => !dedupedMeds.some(d => d.name?.toLowerCase() === m.name?.toLowerCase())
          )
        ];
        basePatient.medications_pre_extracted = true;
      }

      const reqPayload = {
        patient: basePatient,
        sources: finalSources
      };

      const res = await apiClient.post("/analyses/async", reqPayload);
      setAnalysisId(res.data.analysis_id);
      setStatus("polling");
    } catch (err) {
      console.error(err);
      toast.error("Erreur lors du lancement de l'analyse");
      setStatus("error");
      setIsLaunching(false);
    }
  };

  useEffect(() => {
    let interval;
    if (status === "polling" && analysisId) {
      interval = setInterval(async () => {
        try {
          const res = await apiClient.get(`/analyses/${analysisId}/status`);
          const data = res.data;
          setProgress(data.progress);
          setCurrentStep(data.current_step);

          if (data.status === "completed") {
            clearInterval(interval);
            setStatus("completed");
            toast.success("Analyse terminée !");
            navigate(`/analysis/${analysisId}`);
          } else if (data.status === "error") {
            clearInterval(interval);
            setStatus("error");
            toast.error(data.error_message || "Erreur durant l'analyse");
          }
        } catch (e) {
          console.error("Polling error", e);
        }
      }, 2000);
    }
    return () => clearInterval(interval);
  }, [status, analysisId, navigate]);

  if (status === "polling" || status === "launching") {
    return (
      <div className="max-w-3xl mx-auto p-6 space-y-6">
        <AnalysisProgress progress={progress} currentStep={currentStep || ""} />
        <DisclaimerV1 />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Nouvelle analyse</h1>
        <p className="text-gray-500 mt-1">Combinez différentes sources d'information pour une analyse complète.</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600" />
            Documents (PDF)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
              isDragActive ? "border-blue-500 bg-blue-50" : "border-gray-300 hover:border-gray-400"
            }`}
          >
            <input {...getInputProps()} />
            <Upload className="w-10 h-10 text-gray-400 mx-auto mb-4" />
            <p className="text-sm text-gray-600">Glisser-déposer ou <span className="text-blue-600 font-medium">Choisir des fichiers</span></p>
          </div>

          {/* Compteur global quand plusieurs docs sont en traitement */}
          {sources.filter(s => s.status === 'uploading').length > 1 && (
            <div className="flex items-center gap-2 text-sm text-blue-700 bg-blue-50 px-3 py-2 rounded-md border border-blue-200">
              <div className="w-3.5 h-3.5 border-2 border-blue-400 border-t-blue-700 rounded-full animate-spin flex-shrink-0" />
              <span>
                Traitement en cours — {sources.filter(s => s.status === 'uploading').length} document(s) restant(s)
              </span>
            </div>
          )}

          {sources.map(src => (
            <div key={src.id}>
              <div
                className={`flex items-center gap-4 p-3 rounded-md border ${
                  src.status === 'error'
                    ? 'bg-red-50 border-red-200'
                    : src.status === 'uploading'
                    ? 'bg-blue-50/40 border-blue-200'
                    : 'bg-gray-50 border-gray-200'
                }`}
              >
                <span className="flex-1 font-medium truncate min-w-0">
                  {src.file_name}
                  {src.status === 'uploading' && (
                    <span className="text-blue-600 text-xs ml-2 inline-flex items-center gap-1">
                      <span className="inline-block w-2.5 h-2.5 border border-blue-400 border-t-blue-700 rounded-full animate-spin" />
                      {src.statusMsg || "En cours…"}
                    </span>
                  )}
                  {src.status === 'error' && (
                    <span className="text-red-600 text-xs ml-2">{src.statusMsg}</span>
                  )}
                </span>
                <select
                  className={`border rounded p-1 text-sm bg-white ${src.status === 'uploading' ? 'opacity-40 pointer-events-none' : ''}`}
                  value={src.doc_type}
                  onChange={(e) => updateSource(src.id, "doc_type", e.target.value)}
                  disabled={src.status === 'uploading'}
                >
                  {docTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
                <input
                  type="date"
                  className={`border rounded p-1 text-sm bg-white ${src.status === 'uploading' ? 'opacity-40 pointer-events-none' : ''}`}
                  value={src.doc_date || ""}
                  onChange={(e) => updateSource(src.id, "doc_date", e.target.value)}
                  disabled={src.status === 'uploading'}
                />
                <Button variant="ghost" size="icon" onClick={() => removeSource(src.id)}>
                  <Trash2 className={`w-4 h-4 ${src.status === 'error' ? 'text-red-500' : 'text-red-400'}`} />
                </Button>
              </div>

              {process.env.REACT_APP_OCR_DEBUG === "true" && src.ocr_comparison && (
                <OCRComparisonPanel ocrData={src.ocr_comparison} />
              )}
            </div>
          ))}
          {sources.some(s => s.status === 'ready') && (
            <div className="flex items-start gap-2 text-sm text-blue-800 bg-blue-50 p-3 rounded-md">
              <Info className="w-4 h-4 mt-0.5 shrink-0" />
              <p>Type et date détectés automatiquement — veuillez vérifier.</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Stethoscope className="w-5 h-5 text-green-600" />
            Anamnèse rapportée par le patient
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <textarea
            className="w-full border rounded-md p-3 text-sm focus:ring-2 focus:ring-blue-500 min-h-[100px]"
            placeholder="Ex : 'En mai 2002, hospitalisé pour...'"
            value={anamnese}
            onChange={(e) => setAnamnese(e.target.value)}
          />
          <div className="flex items-start gap-2 text-sm text-green-800 bg-green-50 p-3 rounded-md">
            <Info className="w-4 h-4 mt-0.5 shrink-0" />
            <p>Intégré comme élément déclaratif dans la fiche (Source de fiabilité: déclarative).</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between cursor-pointer">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-purple-600" />
              Formulaire guidé
            </div>
            <ChevronDown className="w-5 h-5 text-gray-400" />
          </CardTitle>
        </CardHeader>
      </Card>

      {(() => {
        const canLaunch =
          consent &&
          (sources.filter(s => s.status !== 'error').length > 0 || anamnese.trim()) &&
          !isLaunching &&
          !sources.some(s => s.status === 'uploading');

        return (
          <div className={`p-6 rounded-lg border shadow-sm space-y-6 transition-colors duration-300 ${
            canLaunch ? 'bg-green-50 border-green-300' : 'bg-white border-gray-200'
          }`}>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                className="mt-1 w-5 h-5 rounded accent-green-600"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
              />
              <span className={`text-sm transition-colors ${consent ? 'text-green-800 font-medium' : 'text-gray-700'}`}>
                Je consens au traitement des données de santé de ce patient et à leur transfert vers les services IA (Gemini — serveurs UE/US) dans le cadre de cet outil documentaire (RGPD Art. 9).
              </span>
            </label>

            <Button
              className={`w-full text-lg h-14 transition-all duration-300 ${
                canLaunch
                  ? 'bg-green-600 hover:bg-green-700 text-white shadow-md shadow-green-200'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
              disabled={!canLaunch}
              onClick={handleLaunch}
            >
              {canLaunch ? '✓ ' : ''}Lancer l'analyse — {sources.filter(s => s.status !== 'error').length + (anamnese.trim() ? 1 : 0)} source(s)
            </Button>
          </div>
        );
      })()}

      {/* AI Pipeline Monitor — visible uniquement si REACT_APP_OCR_DEBUG=true */}
      {lastPipelineMeta && (
        <AIPipelineMonitor
          ocrComparison={lastPipelineMeta.ocrComparison}
          extractionMeta={lastPipelineMeta.extractionMeta}
          glinerMeta={lastPipelineMeta.glinerMeta}
        />
      )}

      <DisclaimerV1 />
    </div>
  );
}
